import {
  AppCard,
  Button,
  FormAsyncAutocomplete,
  Icon,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiContentSaveOutline } from "@mdi/js";
import { Box } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type {
  AssignmentRule,
  AssignmentRuleNew,
} from "../../../@types/services/AssignmentRule";
import type { MmRequestReason } from "../../../@types/services/MmRequestReason";
import type { PlantField } from "../../../@types/services/PlantField";
import type { RequesterArea } from "../../../@types/services/RequesterArea";
import type { User } from "../../../@types/services/User";
import { listPlants } from "../../../__generated__/orval/endpoints/plants/plants";
import type { ListPlantsParams } from "../../../__generated__/orval/models/listPlantsParams.zod";
import { ForbiddenError } from "../../../errors/ForbiddenError";
import { createAssignmentRule } from "../../../services/assignmentRules";
import { fetchMmRequestReasons } from "../../../services/mmRequestReasons";
import { fetchPlantFields } from "../../../services/plantFields";
import { fetchRequesterAreas } from "../../../services/requesterAreas";
import { fetchUsers } from "../../../services/users";
import {
  type NewMmRequestAssignmentRuleFormValues,
  newMmRequestAssignmentRuleFormSchema,
} from "./-schemas/newMmRequestAssignmentRuleFormSchema";

const NewMmRequestAssignmentRule: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { handleSubmit, control } = useForm({
    defaultValues: {
      mm_request_reasons: [],
      plant_fields: [],
      plants: [],
      requester_areas: [],
      user: null,
    },
    mode: "onBlur",
    resolver: zodResolver(newMmRequestAssignmentRuleFormSchema),
  });

  const { mutate: create, isPending: isCreating } = useMutation<
    AssignmentRule,
    Error,
    AssignmentRuleNew
  >({
    mutationFn: (data) => createAssignmentRule(data),
    onError: () => {
      enqueueSnackbar(t("assignmentRules.messages.creationError"), {
        variant: "error",
      });
    },
    onSuccess: () => {
      enqueueSnackbar(t("assignmentRules.messages.creationSuccess"), {
        variant: "success",
      });
      queryClient.invalidateQueries({
        queryKey: ["mmRequestAssignmentRules"],
      });
      navigate({ to: "/mm_request_assignment_rules" });
    },
  });

  const onSubmit: SubmitHandler<NewMmRequestAssignmentRuleFormValues> =
    useCallback(
      (data) => {
        const formattedData: AssignmentRuleNew = {
          mm_request_reasons_attributes: data.mm_request_reasons.map(
            (mmRequestReason) => ({
              mm_request_reason_id: mmRequestReason.id,
            }),
          ),
          plant_fields_attributes: data.plant_fields.map((plantField) => ({
            plant_field_id: plantField.id,
          })),
          plants_attributes: data.plants.map((plant) => ({
            plant_id: plant.id,
          })),
          requester_areas_attributes: data.requester_areas.map(
            (requesterArea) => ({
              requester_area_id: requesterArea.id,
            }),
          ),
          user_id: data.user?.id ?? 0,
        };

        create(formattedData);
      },
      [create],
    );

  return (
    <PageContainer
      maxWidth="md"
      showBackButton
      title={t("assignmentRules.titles.new")}
    >
      <AppCard>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              width: "100%",
            }}
          >
            <FormAsyncAutocomplete
              control={control}
              fetchOptions={fetchUsers}
              filters={(searchTerm: string) => ({
                name_or_login: searchTerm,
                user_group_name: [
                  "Materials Management Repetro Control",
                  "Materials Management",
                  "MM RSPS3",
                ],
              })}
              getOptionLabel={(option: User) =>
                option ? option?.full_name_with_login : ""
              }
              label={t("assignmentRules.labels.user")}
              minSearchLength={3}
              name="user"
              queryKey={["users", "infinite"]}
              required
            />

            <FormAsyncAutocomplete
              control={control}
              fetchOptions={fetchMmRequestReasons}
              filters={(searchTerm: string) => ({
                enabled: true,
                title_like: searchTerm,
              })}
              getOptionLabel={(option: MmRequestReason) => option.title || ""}
              label={t("assignmentRules.labels.mmRequestReasons")}
              multiple
              name="mm_request_reasons"
              queryKey={["mmRequestReasons", "infinite"]}
              required
            />

            <FormAsyncAutocomplete
              control={control}
              fetchOptions={fetchPlantFields}
              filters={(searchTerm: string) => ({
                active_on_mm_request: true,
                name: searchTerm,
              })}
              getOptionLabel={(option: PlantField) => option.name || ""}
              label={t("assignmentRules.labels.plantFields")}
              multiple
              name="plant_fields"
              queryKey={["plantFields", "infinite"]}
              required
            />

            <FormAsyncAutocomplete
              control={control}
              fetchOptions={fetchRequesterAreas}
              filters={(searchTerm: string) => ({
                enabled: true,
                name: searchTerm,
              })}
              getOptionLabel={(option: RequesterArea) => option.name || ""}
              label={t("assignmentRules.labels.requesterAreas")}
              multiple
              name="requester_areas"
              queryKey={["requesterAreas", "infinite"]}
              required
            />

            <FormAsyncAutocomplete
              control={control}
              fetchOptions={listPlants}
              filters={(searchTerm): ListPlantsParams => ({
                active_on_mm_request: true,
                plant_number_with_name: searchTerm,
              })}
              getOptionLabel={(option) => option.plant_number_with_name}
              helperText={t("assignmentRules.helpers.plants")}
              label={t("assignmentRules.labels.plants")}
              minSearchLength={2}
              multiple
              name="plants"
              queryKey={["plants", "infinite"]}
            />

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                color="success"
                loading={isCreating}
                startIcon={<Icon icon={mdiContentSaveOutline} />}
                type="submit"
                variant="contained"
              >
                {t("common.create")}
              </Button>
            </Box>
          </Box>
        </form>
      </AppCard>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/mm_request_assignment_rules/new",
)({
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("Assignment Rules - Create")) {
      return;
    }

    throw new ForbiddenError();
  },
  component: NewMmRequestAssignmentRule,
});
